import { useState, useEffect } from 'react';
import PhotoUpload from './PhotoUpload';

const InventoryModal = ({ isOpen, onClose, onSave, item = null }) => {
  const [formData, setFormData] = useState({
    modelName: '',
    modelNumber: '',
    size: '',
    retailPrice: '',
    wholesalePrice: '',
    initialStock: '',
    stockAdjustment: '',
  });
  const [photo, setPhoto] = useState('');
  const [selectedPhotoFile, setSelectedPhotoFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = item !== null;

  // Populate form when editing
  useEffect(() => {
    if (item) {
      setFormData({
        modelName: item.modelName || '',
        modelNumber: item.modelNumber || '',
        size: item.size || '',
        retailPrice: item.retailPrice || item.price || '',
        wholesalePrice: item.wholesalePrice || '',
        initialStock: item.initialStock || '',
        stockAdjustment: '',
      });
      setPhoto(item.photo || '');
      setSelectedPhotoFile(null);
    } else {
      // Reset form for new item
      setFormData({
        modelName: '',
        modelNumber: '',
        size: '',
        retailPrice: '',
        wholesalePrice: '',
        initialStock: '',
        stockAdjustment: '',
      });
      setPhoto('');
      setSelectedPhotoFile(null);
    }
    setErrors({});
  }, [item, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handlePhotoFileSelected = (file) => {
    setSelectedPhotoFile(file);
  };

  const uploadPhotoForNewModel = async (inventoryId) => {
    if (!selectedPhotoFile || !inventoryId) return;

    try {
      const formData = new FormData();
      formData.append('photo', selectedPhotoFile);

      const baseUrl = window.location.hostname.includes('trycloudflare.com')
        ? 'https://originally-minolta-wto-finals.trycloudflare.com'
        : '';

      const response = await fetch(`${baseUrl}/api/inventory-photos/upload/${inventoryId}`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Photo uploaded successfully for new model:', result.photo);
      } else {
        console.error('Failed to upload photo for new model');
      }
    } catch (error) {
      console.error('Error uploading photo for new model:', error);
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.modelName.trim()) {
      newErrors.modelName = 'Model name is required';
    }

    if (!formData.modelNumber || formData.modelNumber === '') {
      newErrors.modelNumber = 'Model number is required';
    }

    if (!formData.size) {
      newErrors.size = 'Category (size) is required';
    }

    // Validate model number range based on size
    if (formData.size && formData.modelNumber) {
      const num = parseInt(formData.modelNumber);
      if (isNaN(num)) {
        newErrors.modelNumber = 'Model number must be a valid number';
      } else {
        let validRange = false;
        const sizeLower = formData.size.toLowerCase();
        if (sizeLower === 'small' && num >= 1 && num <= 20) validRange = true;
        if (sizeLower === 'medium' && num >= 21 && num <= 30) validRange = true;
        if (sizeLower === 'large' && num >= 31 && num <= 40) validRange = true;

        if (!validRange) {
          newErrors.modelNumber = `Model number ${num} is not valid for ${formData.size} category.
                                  Small: 1-20, Medium: 21-30, Large: 31-40`;
        }
      }
    }

    if (formData.retailPrice === '' || Number(formData.retailPrice) < 0) {
      newErrors.retailPrice = 'Retail price must be greater than or equal to 0';
    }

    if (formData.wholesalePrice === '' || Number(formData.wholesalePrice) < 0) {
      newErrors.wholesalePrice = 'Wholesale price must be greater than or equal to 0';
    }

    // Check if wholesale price is not higher than retail price
    if (Number(formData.wholesalePrice) > Number(formData.retailPrice)) {
      newErrors.wholesalePrice = 'Wholesale price should not be higher than retail price';
    }

    if (!isEditMode) {
      if (formData.initialStock === '' || Number(formData.initialStock) < 0) {
        newErrors.initialStock = 'Initial stock must be greater than or equal to 0';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode) {
        // Update existing item
        const updateData = {
          modelName: formData.modelName.trim(),
          modelNumber: parseInt(formData.modelNumber),
          size: formData.size.toLowerCase(),
          retailPrice: Number(formData.retailPrice),
          wholesalePrice: Number(formData.wholesalePrice),
        };

        // Only include stockAdjustment if provided
        if (formData.stockAdjustment !== '') {
          updateData.stockAdjustment = Number(formData.stockAdjustment);
        }

        await onSave(item._id, updateData);
      } else {
        // Create new item
        const newItem = {
          modelName: formData.modelName.trim(),
          modelNumber: parseInt(formData.modelNumber),
          size: formData.size.toLowerCase(),
          retailPrice: Number(formData.retailPrice),
          wholesalePrice: Number(formData.wholesalePrice),
          initialStock: Number(formData.initialStock),
        };

        const result = await onSave(null, newItem);
        // Upload photo if one was selected
        if (selectedPhotoFile && result && result._id) {
          await uploadPhotoForNewModel(result._id);
        }
      }

      // Reset form and close modal
      setFormData({
        modelName: '',
        modelNumber: '',
        size: '',
        retailPrice: '',
        wholesalePrice: '',
        initialStock: '',
        stockAdjustment: '',
      });
      onClose();
    } catch (error) {
      // Error handling is done in parent component
      console.error('Error saving inventory:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-800">
              {isEditMode ? 'Edit Inventory Item' : 'Add New Model'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              {/* Model Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model Name *
                </label>
                <input
                  type="text"
                  name="modelName"
                  value={formData.modelName}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    errors.modelName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., Small Traditional Ganesh"
                />
                {errors.modelName && (
                  <p className="text-red-500 text-sm mt-1">{errors.modelName}</p>
                )}
              </div>

              {/* Model Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model Number *
                </label>
                <input
                  type="text"
                  name="modelNumber"
                  value={formData.modelNumber}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    errors.modelNumber ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., GAN-001-S"
                />
                {errors.modelNumber && (
                  <p className="text-red-500 text-sm mt-1">{errors.modelNumber}</p>
                )}
              </div>

              {/* Size/Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category (Size) *
                </label>
                <select
                  name="size"
                  value={formData.size}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    errors.size ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select category</option>
                  <option value="small">Small (Model numbers: 1-20)</option>
                  <option value="medium">Medium (Model numbers: 21-30)</option>
                  <option value="large">Large (Model numbers: 31-40)</option>
                </select>
                {errors.size && (
                  <p className="text-red-500 text-sm mt-1">{errors.size}</p>
                )}
                <p className="text-gray-500 text-xs mt-1">
                  Choose category to validate model number range
                </p>
              </div>

              {/* Retail Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Retail Price (₹) *
                </label>
                <input
                  type="number"
                  name="retailPrice"
                  value={formData.retailPrice}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    errors.retailPrice ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="0.00"
                />
                {errors.retailPrice && (
                  <p className="text-red-500 text-sm mt-1">{errors.retailPrice}</p>
                )}
                <p className="text-gray-500 text-xs mt-1">
                  Price for individual retail customers
                </p>
              </div>

              {/* Wholesale Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Wholesale Price (₹) *
                </label>
                <input
                  type="number"
                  name="wholesalePrice"
                  value={formData.wholesalePrice}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    errors.wholesalePrice ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="0.00"
                />
                {errors.wholesalePrice && (
                  <p className="text-red-500 text-sm mt-1">{errors.wholesalePrice}</p>
                )}
                <p className="text-gray-500 text-xs mt-1">
                  Price for bulk wholesale orders (should be ≤ retail price)
                </p>
              </div>

              {/* Initial Stock (for new items) or Stock Adjustment (for edits) */}
              {!isEditMode ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Initial Stock *
                  </label>
                  <input
                    type="number"
                    name="initialStock"
                    value={formData.initialStock}
                    onChange={handleChange}
                    min="0"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      errors.initialStock ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0"
                  />
                  {errors.initialStock && (
                    <p className="text-red-500 text-sm mt-1">{errors.initialStock}</p>
                  )}
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stock Adjustment
                  </label>
                  <input
                    type="number"
                    name="stockAdjustment"
                    value={formData.stockAdjustment}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="+10 or -5 (leave empty for no change)"
                  />
                  <p className="text-gray-500 text-xs mt-1">
                    Current Stock: {item?.currentStock || 0} | 
                    Use positive to add, negative to subtract
                  </p>
                </div>
              )}

              {/* Photo Upload */}
              <PhotoUpload
                inventoryId={item?._id || null}
                currentPhoto={photo}
                onPhotoUpdate={setPhoto}
                onFileSelected={handlePhotoFileSelected}
                isNewModel={!isEditMode}
              />
            </div>

            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InventoryModal;
